/* global QuickAddService */

class ProductMappedProductsSwitcher extends HTMLElement {
  constructor() {
    super();
    this.inputs = [];
    this.sectionId = this.getAttribute('data-section-id');
    this.productCache = new Map();
    this.onChange = this.onChange.bind(this);
  }

  connectedCallback() {
    this.inputs = Array.from(this.querySelectorAll('input[name="sibling-product"]'));
    this.attachEvents();

    // Preload siblings when idle
    if ('requestIdleCallback' in window) {
      requestIdleCallback(() => this.preloadSiblings());
    } else {
      setTimeout(() => this.preloadSiblings(), 300);
    }
  }

  get isInQuickAddModal() {
    if (this.sectionId?.startsWith('quickadd-')) return true;
    return this.closest('quick-add-modal') !== null;
  }

  get quickAddModal() {
    return this.closest('quick-add-modal');
  }

  disconnectedCallback() {
    this.detachEvents();
  }

  attachEvents() {
    this.inputs.forEach((input) => input.addEventListener('change', this.onChange));
  }

  detachEvents() {
    this.inputs.forEach((input) => input.removeEventListener('change', this.onChange));
  }

  async onChange(event) {
    const target = event.target;

    let currentSizeOptionValue = null;
    const productInfo = this.closest('product-info');
    if (productInfo) {
      const currentSizeOptionPath = 'variant-selects fieldset.option-size input[type="radio"]:checked';
      const currentSizeVariant = productInfo.querySelector(currentSizeOptionPath);
      currentSizeOptionValue = currentSizeVariant?.value || null;
    }

    this.inputs.forEach((input) => {
      input.checked = false;
    });

    target.checked = true;

    if (!target?.value) return;

    const handle = target.value;

    try {
      const result = await this.getSectionForHandle(handle);
      const newSection = result.desktopSection;
      const mobileSection = result.mobileSection;
      
      if (!newSection) return;

      const newSectionClone = newSection.cloneNode(true);
      const newMobileSectionClone = mobileSection?.cloneNode(true) ?? null;

      const currentSection = document.getElementById(`MainProduct-${this.sectionId}`);
      if (!currentSection) return;

      // Update the URL to the new product
      if (!this.isInQuickAddModal) {
        const url = new URL(window.location.href);
        url.pathname = `/products/${handle}`;
        url.search = '';

        window.history.replaceState({ path: url.toString() }, '', url.toString());
      }

      this.preserveNonReplacable(currentSection, newSectionClone);
      
      // Replace <product-modal> 
      try {
        const newProductModal = newSectionClone.querySelector(`#ProductModal-${this.sectionId}`);
        if (newProductModal) {
          const existingProductModal = document.getElementById(`ProductModal-${this.sectionId}`);
          if (existingProductModal) {
            existingProductModal.replaceWith(newProductModal);
          }
        }
      } catch (modalErr) {
        console.warn('[SiblingProductSwitcher] Failed to replace product modal', modalErr);
      }
      
      // Update Desktop Section
      currentSection.replaceWith(newSectionClone);
      
      // Update Mobile Section
      try {
        const currentMobileSection = document.getElementById(`shopify-section-${window.mobileProductInfoSectionId}`);
        if (currentMobileSection && newMobileSectionClone) {
          currentMobileSection.replaceWith(newMobileSectionClone);
        }
      } catch (mobileErr) {
        console.warn('[SiblingProductSwitcher] Failed to replace mobile section', mobileErr);
      }

      // Enable zoom on hover
      if (typeof enableZoomOnHover === 'function') {
        try {
          enableZoomOnHover(2);
        } catch (zoomErr) {
          console.warn('[SiblingProductSwitcher] Failed to enable zoom on hover', zoomErr);
        }
      }

      // Persist the selected size option value accross mapped products
      const productInfo = this.closest('product-info');
      if (!productInfo) return;
      const variantSelects = productInfo.querySelector('variant-selects');
      if (!variantSelects) return;

      const previousSizeOptionInput = variantSelects.querySelector('input[value="' + currentSizeOptionValue + '"]');
      if (previousSizeOptionInput) {
        previousSizeOptionInput.checked = true;
        setTimeout(() => {
          const changeEvent = new Event('change');
          previousSizeOptionInput.dispatchEvent(changeEvent);
          variantSelects.dispatchEvent(changeEvent);
        }, 100);
      }
    } catch (e) {
      console.error('[SiblingProductSwitcher] Failed to switch product', e);
    }
  }

  async getSectionForHandle(handle) {
    const mobileSectionCacheKey = handle + '-mobile';
    const parser = new DOMParser();

    // Check if both sections are cached
    if (this.productCache.has(handle)) {
      const cachedDesktop = this.productCache.get(handle);
      const cachedMobile = this.productCache.get(mobileSectionCacheKey) ?? null;
      
      // If mobile section ID exists but not cached, try to fetch it
      if (window.mobileProductInfoSectionId && !cachedMobile) {
        const mobileSection = await this.fetchMobileSection(handle, parser);
        return {
          desktopSection: cachedDesktop,
          mobileSection: mobileSection
        };
      }
      
      return {
        desktopSection: cachedDesktop,
        mobileSection: cachedMobile
      };
    }

    // There is an edge case, quick add modal overrides the section id by adding 'quickadd-' prefix, so we need to remove it.
    const sectionId = this.sectionId.replace('quickadd-', '');

    const response = await fetch(`/products/${handle}?section_id=${sectionId}`);
    const htmlText = await response.text();

    const doc = parser.parseFromString(htmlText, 'text/html');
    const section = doc.getElementById(`MainProduct-${sectionId}`);

    if (this.isInQuickAddModal && section) {
      quickAddService.preprocessHTML(section, this.quickAddModal.modalContent);
    }

    if (section) this.productCache.set(handle, section);

    // Fetch mobile section separately with error handling
    const mobileSection = await this.fetchMobileSection(handle, parser);

    return {
      desktopSection: section,
      mobileSection: mobileSection
    };
  }

  async fetchMobileSection(handle, parser) {
    const mobileSectionCacheKey = handle + '-mobile';
    
    if (this.productCache.has(mobileSectionCacheKey)) {
      return this.productCache.get(mobileSectionCacheKey);
    }

    if (!window.mobileProductInfoSectionId) {
      return null;
    }

    try {
      const mobileSectionId = window.mobileProductInfoSectionId;
      const mobileSectionResponse = await fetch(`/products/${handle}?section_id=${mobileSectionId}`);
      const mobileHtmlText = await mobileSectionResponse.text();
      const mobileDoc = parser.parseFromString(mobileHtmlText, 'text/html');
      const mobileSection = mobileDoc.getElementById(`shopify-section-${mobileSectionId}`);
      
      if (mobileSection) {
        this.productCache.set(mobileSectionCacheKey, mobileSection);
      }
      
      return mobileSection;
    } catch (err) {
      console.warn('[SiblingProductSwitcher] Failed to fetch mobile section', err);
      return null;
    }
  }

  preloadSiblings() {
    this.inputs.forEach((input) => {
      const handle = input.value;
      if (!this.productCache.has(handle)) {
        this.getSectionForHandle(handle).catch(() => { });
        this.fetchMobileSection(handle).catch(() => { });
      }
    });
  }

  preserveNonReplacable(currentSection, incomingSection) {
    try {
      const currentList = Array.from(
        currentSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );
      if (!currentList.length) return;

      const incomingList = Array.from(
        incomingSection.querySelectorAll('[data-sibling-product-switcher-non-replacable]')
      );

      const count = Math.min(currentList.length, incomingList.length);
      for (let i = 0; i < count; i++) {
        const keepNode = currentList[i];
        const replaceTarget = incomingList[i];
        if (!keepNode || !replaceTarget) continue;
        replaceTarget.replaceWith(keepNode);
      }
    } catch (err) {
      console.warn('[SiblingProductSwitcher] preserveNonReplacable failed', err);
    }
  }
}

if (!customElements.get('product-mapped-products-switcher')) {
  customElements.define('product-mapped-products-switcher', ProductMappedProductsSwitcher);
}